import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Card, Accordion, Badge } from 'react-bootstrap';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import * as Icon from 'react-feather';

const createEmptySatLookup = () => ({
    records: [],
    states: [],
    municipalities: [],
    localities: [],
    colonies: []
});

const createDefaultStop = () => ({
    origin_address: '',
    destination_address: '',
    origin_postal_code: '',
    destination_postal_code: '',
    distance_km: '',
    origin_rfc: '',
    destination_rfc: '',
    origin_estado: '',
    destination_estado: '',
    origin_municipio: '',
    destination_municipio: '',
    origin_localidad: '',
    destination_localidad: '',
    origin_colonia: '',
    destination_colonia: '',
    origin_calle: '',
    destination_calle: '',
    origin_google_estado: '',
    destination_google_estado: '',
    origin_google_municipio: '',
    destination_google_municipio: '',
    origin_google_localidad: '',
    destination_google_localidad: ''
});

function DefaultModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const [routeData, setRouteData] = useState({
        client_id: '',
        name: '',
        price: '',
        is_local: false
    });

    const [clients, setClients] = useState([]);
    const [stops, setStops] = useState([createDefaultStop()]);
    const [satLookups, setSatLookups] = useState([{ origin: createEmptySatLookup(), destination: createEmptySatLookup() }]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (props.show) {
            loadClients();
            resetForm();
        }
    }, [props.show]);

    const resetForm = () => {
        setRouteData({
            client_id: '',
            name: '',
            price: '',
            is_local: false
        });
        setStops([createDefaultStop()]);
        setSatLookups([{ origin: createEmptySatLookup(), destination: createEmptySatLookup() }]);
    };

    const loadClients = async () => {
        try {
            const response = await axios.get(`${apiUrl}/finance/get_all_clients`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setClients(response.data.msg.Clients || []);
        } catch (error) {
            console.error('Error loading clients:', error);
            alert('Error al cargar clientes');
        }
    };

    const handleRouteDataChange = (field, value) => {
        setRouteData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleStopChange = (index, value, field) => {
        const updatedStops = [...stops];
        updatedStops[index] = {
            ...updatedStops[index],
            [field]: value
        };
        setStops(updatedStops);
    };

    const setSatLookupForStop = (index, prefix, lookup) => {
        setSatLookups(prev => {
            const next = [...prev];
            while (next.length <= index) {
                next.push({ origin: createEmptySatLookup(), destination: createEmptySatLookup() });
            }
            next[index] = {
                ...next[index],
                [prefix]: lookup
            };
            return next;
        });
    };

    const fetchSatLookup = async (postalCode, localityHint = '') => {
        const response = await axios.get(`${apiUrl}/finance/sat_location_options`, {
            params: {
                postal_code: postalCode,
                locality_hint: localityHint || '',
            },
            headers: {
                Authorization: authHeader()
            }
        });
        return response.data.msg || createEmptySatLookup();
    };

    const applySatDefaults = (index, prefix, lookup) => {
        setStops(prev => {
            const next = [...prev];
            const stop = { ...next[index] };
            const stateField = `${prefix}_estado`;
            const municipalityField = `${prefix}_municipio`;
            const localityField = `${prefix}_localidad`;
            const colonyField = `${prefix}_colonia`;

            const stateOptions = lookup.states || [];
            const municipalityOptions = lookup.municipalities || [];
            const localityOptions = lookup.localities || [];
            const colonyOptions = lookup.colonies || [];

            if ((!stop[stateField] || !stateOptions.some(option => option.code === stop[stateField])) && stateOptions.length === 1) {
                stop[stateField] = stateOptions[0].code;
            }
            if (!stop[municipalityField] || !municipalityOptions.some(option => option.code === stop[municipalityField])) {
                stop[municipalityField] = municipalityOptions.length === 1 ? municipalityOptions[0].code : '';
            }
            if (!stop[localityField] || !localityOptions.some(option => option.code === stop[localityField])) {
                if (lookup.suggested_locality_code && localityOptions.some(option => option.code === lookup.suggested_locality_code)) {
                    stop[localityField] = lookup.suggested_locality_code;
                } else if (localityOptions.length === 1) {
                    stop[localityField] = localityOptions[0].code;
                } else {
                    stop[localityField] = '';
                }
            }
            if (!stop[colonyField] || !colonyOptions.some(option => option.code === stop[colonyField])) {
                stop[colonyField] = colonyOptions.length === 1 ? colonyOptions[0].code : '';
            }

            next[index] = stop;
            return next;
        });
    };

    const lookupSatOptionsForPostalCode = async (index, prefix, postalCode, localityHint = '') => {
        if (!postalCode || postalCode.length !== 5) {
            setSatLookupForStop(index, prefix, createEmptySatLookup());
            return;
        }

        try {
            const lookup = await fetchSatLookup(postalCode, localityHint);
            setSatLookupForStop(index, prefix, lookup);
            applySatDefaults(index, prefix, lookup);
        } catch (error) {
            console.error('Error loading SAT location options:', error);
            setSatLookupForStop(index, prefix, createEmptySatLookup());
        }
    };

    const handlePostalCodeChange = (index, prefix, value) => {
        const field = `${prefix}_postal_code`;
        const normalizedValue = value.replace(/\D/g, '').slice(0, 5);
        handleStopChange(index, normalizedValue, field);
        const currentStop = stops[index] || {};
        const localityHint = currentStop[`${prefix}_google_localidad`] || currentStop[`${prefix}_address`] || '';
        if (normalizedValue.length === 5) {
            lookupSatOptionsForPostalCode(index, prefix, normalizedValue, localityHint);
        } else {
            setSatLookupForStop(index, prefix, createEmptySatLookup());
        }
    };

    const buildOptionMap = (records, codeField, nameField) => {
        const map = new Map();
        records.forEach((record) => {
            const code = record[codeField];
            const name = record[nameField];
            if (!code && !name) return;
            if (!map.has(code)) {
                map.set(code, {
                    code,
                    name: name || '',
                    label: code && name ? `${code} - ${name}` : code || name || ''
                });
            }
        });
        return Array.from(map.values());
    };

    const getSatFieldOptions = (index, prefix) => {
        const lookup = satLookups[index]?.[prefix] || createEmptySatLookup();
        const records = lookup.records || [];
        const stop = stops[index] || {};
        const stateCode = stop[`${prefix}_estado`] || '';
        const municipalityCode = stop[`${prefix}_municipio`] || '';
        const localityCode = stop[`${prefix}_localidad`] || '';

        const stateRecords = records;
        const municipalityRecords = stateCode
            ? records.filter(record => record.state_code === stateCode)
            : records;
        const localityRecords = stateCode || municipalityCode
            ? records.filter(record =>
                (!stateCode || record.state_code === stateCode) &&
                (!municipalityCode || record.municipality_code === municipalityCode)
            )
            : records;
        const colonyRecords = stateCode || municipalityCode || localityCode
            ? records.filter(record =>
                (!stateCode || record.state_code === stateCode) &&
                (!municipalityCode || record.municipality_code === municipalityCode) &&
                (!localityCode || record.locality_code === localityCode)
            )
            : records;

        const stateOptions = buildOptionMap(stateRecords, 'state_code', 'state_name');
        const municipalityOptions = buildOptionMap(municipalityRecords, 'municipality_code', 'municipality_name');
        const localityOptions = buildOptionMap(localityRecords, 'locality_code', 'locality_name');
        const colonyOptions = buildOptionMap(colonyRecords, 'colony_code', 'colony_name');

        return {
            states: stateOptions.length ? stateOptions : (lookup.states || []),
            municipalities: municipalityOptions.length ? municipalityOptions : (lookup.municipalities || []),
            localities: localityOptions.length ? localityOptions : (lookup.localities || []),
            colonies: colonyOptions.length ? colonyOptions : (lookup.colonies || []),
        };
    };

    const extractAddressDetails = (results) => {
        if (!results || results.length === 0) {
            return {
                postal_code: '',
                estado: '',
                municipio: '',
                localidad: '',
                colonia: '',
                calle: '',
            };
        }
        const addressComponents = results[0].address_components || [];
        const findComponent = (type, useShort = false) => {
            const component = addressComponents.find(c => c.types.includes(type));
            if (!component) return '';
            return useShort ? component.short_name : component.long_name;
        };

        return {
            postal_code: findComponent('postal_code'),
            estado: findComponent('administrative_area_level_1', true) || findComponent('administrative_area_level_1'),
            municipio: findComponent('administrative_area_level_2'),
            localidad: findComponent('locality') || findComponent('sublocality') || findComponent('administrative_area_level_3'),
            colonia: findComponent('sublocality_level_1') || findComponent('neighborhood'),
            calle: findComponent('route'),
        };
    };

    const calculateDistance = useCallback((index, updatedStops) => {
        const stop = updatedStops[index];
        if (stop.origin_address && stop.destination_address && window.google) {
            const service = new window.google.maps.DistanceMatrixService();
            service.getDistanceMatrix(
                {
                    origins: [stop.origin_address],
                    destinations: [stop.destination_address],
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    unitSystem: window.google.maps.UnitSystem.METRIC,
                },
                (response, status) => {
                    if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
                        const distanceInKm = (response.rows[0].elements[0].distance.value / 1000).toFixed(1);
                        setStops(prev => {
                            const updated = [...prev];
                            updated[index] = { ...updated[index], distance_km: distanceInKm };
                            return updated;
                        });
                    }
                }
            );
        }
    }, []);

    const handleAddressSelect = async (index, value, field) => {
        const label = value?.label || '';
        const placeId = value?.value?.place_id;
        const cpField = field === 'origin_address' ? 'origin_postal_code' : 'destination_postal_code';
        const prefix = field === 'origin_address' ? 'origin' : 'destination';

        const updatedStops = [...stops];
        updatedStops[index] = { ...updatedStops[index], [field]: label };
        let addressDetails = null;

        if (placeId) {
            try {
                const results = await geocodeByPlaceId(placeId);
                addressDetails = extractAddressDetails(results);
                updatedStops[index] = {
                    ...updatedStops[index],
                    [cpField]: addressDetails.postal_code,
                    [`${prefix}_colonia`]: addressDetails.colonia,
                    [`${prefix}_calle`]: addressDetails.calle,
                    [`${prefix}_google_estado`]: addressDetails.estado,
                    [`${prefix}_google_municipio`]: addressDetails.municipio,
                    [`${prefix}_google_localidad`]: addressDetails.localidad,
                };
            } catch (err) {
                console.error('Error geocoding:', err);
            }
        }

        setStops(updatedStops);
        calculateDistance(index, updatedStops);

        if (updatedStops[index][cpField] && updatedStops[index][cpField].length === 5) {
            lookupSatOptionsForPostalCode(
                index,
                prefix,
                updatedStops[index][cpField],
                addressDetails?.localidad || label
            );
        }
    };

    const addStop = () => {
        const newStop = createDefaultStop();
        setStops([...stops, newStop]);
        setSatLookups([...satLookups, { origin: createEmptySatLookup(), destination: createEmptySatLookup() }]);
    };

    const removeStop = (index) => {
        if (stops.length > 1) {
            const updatedStops = stops.filter((_, i) => i !== index);
            setStops(updatedStops);
            setSatLookups(satLookups.filter((_, i) => i !== index));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            const sanitizedStops = stops.map((stop) => ({
                origin_address: stop.origin_address,
                destination_address: stop.destination_address,
                origin_postal_code: stop.origin_postal_code,
                destination_postal_code: stop.destination_postal_code,
                distance_km: stop.distance_km,
                origin_rfc: stop.origin_rfc,
                destination_rfc: stop.destination_rfc,
                origin_estado: stop.origin_estado,
                destination_estado: stop.destination_estado,
                origin_municipio: stop.origin_municipio,
                destination_municipio: stop.destination_municipio,
                origin_localidad: stop.origin_localidad,
                destination_localidad: stop.destination_localidad,
                origin_colonia: stop.origin_colonia,
                destination_colonia: stop.destination_colonia,
                origin_calle: stop.origin_calle,
                destination_calle: stop.destination_calle,
            }));
            const payload = {
                client_id: routeData.client_id,
                name: routeData.name,
                price: routeData.price,
                is_local: routeData.is_local,
                stops: sanitizedStops
            };

            await axios.post(`${apiUrl}/finance/add_route`, payload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            props.setData();
            props.onHide();
        } catch (error) {
            console.error('Error adding route:', error);
            alert('Error al agregar la ruta');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Agregar Ruta
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Container>
                    <Form onSubmit={handleSubmit}>
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información de la Ruta</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Nombre de Ruta *</Form.Label>
                                        <Form.Control
                                            value={routeData.name}
                                            onChange={(e) => handleRouteDataChange('name', e.target.value)}
                                            placeholder="Ej: Ruta Norte"
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Cliente *</Form.Label>
                                        <Form.Select
                                            value={routeData.client_id}
                                            onChange={(e) => handleRouteDataChange('client_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccionar cliente...</option>
                                            {clients.map((client) => (
                                                <option key={client.id} value={client.id}>
                                                    {client.company_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Row>

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Precio</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={routeData.price}
                                            onChange={(e) => handleRouteDataChange('price', e.target.value)}
                                            placeholder="$0.00"
                                            pattern="[0-9.,]+"
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Tipo de Viaje</Form.Label>
                                        <Form.Select
                                            value={routeData.is_local}
                                            onChange={(e) => handleRouteDataChange('is_local', e.target.value === 'true')}
                                        >
                                            <option value={true}>Local</option>
                                            <option value={false}>Foraneo</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Paradas de la Ruta</h6>
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={addStop}
                                >
                                    <Icon.Plus size={16} /> Agregar Parada
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                <Accordion defaultActiveKey="0">
                                    {stops.map((stop, index) => (
                                        <Accordion.Item eventKey={index.toString()} key={index}>
                                            <Accordion.Header>
                                                <div className="d-flex justify-content-between w-100 me-3">
                                                    <span>
                                                        Parada {index + 1}: {stop.origin_address || 'Sin origen'} → {stop.destination_address || 'Sin destino'}
                                                    </span>
                                                    {stops.length > 1 && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeStop(index);
                                                            }}
                                                        >
                                                            <Icon.X size={12} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={6}>
                                                        <Form.Label>Dirección Origen</Form.Label>
                                                        <GooglePlacesAutocomplete
                                                            apiKey="AIzaSyD4TV67BkLPPGMbnmcLLFqGKpEQ1OX1Y8I"
                                                            selectProps={{
                                                                value: stop.origin_address ? { label: stop.origin_address, value: stop.origin_address } : null,
                                                                onChange: (value) => handleAddressSelect(index, value, 'origin_address'),
                                                                placeholder: 'Buscar dirección...',
                                                                isClearable: true
                                                            }}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={6}>
                                                        <Form.Label>Dirección Destino</Form.Label>
                                                        <GooglePlacesAutocomplete
                                                            apiKey="AIzaSyD4TV67BkLPPGMbnmcLLFqGKpEQ1OX1Y8I"
                                                            selectProps={{
                                                                value: stop.destination_address ? { label: stop.destination_address, value: stop.destination_address } : null,
                                                                onChange: (value) => handleAddressSelect(index, value, 'destination_address'),
                                                                placeholder: 'Buscar dirección...',
                                                                isClearable: true
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>C.P. Origen</Form.Label>
                                                        <Form.Control
                                                            value={stop.origin_postal_code || ''}
                                                            onChange={(e) => handlePostalCodeChange(index, 'origin', e.target.value)}
                                                            placeholder="Ej: 64000"
                                                            maxLength={5}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>C.P. Destino</Form.Label>
                                                        <Form.Control
                                                            value={stop.destination_postal_code || ''}
                                                            onChange={(e) => handlePostalCodeChange(index, 'destination', e.target.value)}
                                                            placeholder="Ej: 66220"
                                                            maxLength={5}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Distancia (km)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="0.1"
                                                            value={stop.distance_km || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'distance_km')}
                                                            placeholder="Distancia en km"
                                                        />
                                                    </Form.Group>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={6}>
                                                        <Form.Label>RFC Origen</Form.Label>
                                                        <Form.Control
                                                            value={stop.origin_rfc || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value.toUpperCase(), 'origin_rfc')}
                                                            placeholder="Ej: XAXX010101000"
                                                            maxLength={13}
                                                            style={{ textTransform: 'uppercase' }}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={6}>
                                                        <Form.Label>RFC Destino</Form.Label>
                                                        <Form.Control
                                                            value={stop.destination_rfc || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value.toUpperCase(), 'destination_rfc')}
                                                            placeholder="Ej: XAXX010101000"
                                                            maxLength={13}
                                                            style={{ textTransform: 'uppercase' }}
                                                        />
                                                    </Form.Group>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Estado SAT Origen</Form.Label>
                                                        <Form.Select
                                                            value={stop.origin_estado || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value.toUpperCase(), 'origin_estado')}
                                                        >
                                                            <option value="">Seleccionar estado SAT...</option>
                                                            {getSatFieldOptions(index, 'origin').states.map((option) => (
                                                                <option key={`origin-state-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Municipio SAT Origen</Form.Label>
                                                        <Form.Select
                                                            value={stop.origin_municipio || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'origin_municipio')}
                                                        >
                                                            <option value="">Seleccionar municipio SAT...</option>
                                                            {getSatFieldOptions(index, 'origin').municipalities.map((option) => (
                                                                <option key={`origin-municipality-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Localidad SAT Origen</Form.Label>
                                                        <Form.Select
                                                            value={stop.origin_localidad || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'origin_localidad')}
                                                        >
                                                            <option value="">Seleccionar localidad SAT...</option>
                                                            {getSatFieldOptions(index, 'origin').localities.map((option) => (
                                                                <option key={`origin-locality-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Colonia SAT Origen</Form.Label>
                                                        <Form.Select
                                                            value={stop.origin_colonia || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'origin_colonia')}
                                                        >
                                                            <option value="">Seleccionar colonia SAT...</option>
                                                            {getSatFieldOptions(index, 'origin').colonies.map((option) => (
                                                                <option key={`origin-colony-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Estado SAT Destino</Form.Label>
                                                        <Form.Select
                                                            value={stop.destination_estado || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value.toUpperCase(), 'destination_estado')}
                                                        >
                                                            <option value="">Seleccionar estado SAT...</option>
                                                            {getSatFieldOptions(index, 'destination').states.map((option) => (
                                                                <option key={`destination-state-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Municipio SAT Destino</Form.Label>
                                                        <Form.Select
                                                            value={stop.destination_municipio || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'destination_municipio')}
                                                        >
                                                            <option value="">Seleccionar municipio SAT...</option>
                                                            {getSatFieldOptions(index, 'destination').municipalities.map((option) => (
                                                                <option key={`destination-municipality-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Localidad SAT Destino</Form.Label>
                                                        <Form.Select
                                                            value={stop.destination_localidad || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'destination_localidad')}
                                                        >
                                                            <option value="">Seleccionar localidad SAT...</option>
                                                            {getSatFieldOptions(index, 'destination').localities.map((option) => (
                                                                <option key={`destination-locality-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={4}>
                                                        <Form.Label>Colonia SAT Destino</Form.Label>
                                                        <Form.Select
                                                            value={stop.destination_colonia || ''}
                                                            onChange={(e) => handleStopChange(index, e.target.value, 'destination_colonia')}
                                                        >
                                                            <option value="">Seleccionar colonia SAT...</option>
                                                            {getSatFieldOptions(index, 'destination').colonies.map((option) => (
                                                                <option key={`destination-colony-${option.code}`} value={option.code}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Row>

                                                <Card className="border-0 bg-light mt-3">
                                                    <Card.Body className="py-3">
                                                        <Row className="align-items-center">
                                                            <Col md={5} className="text-center">
                                                                <div className="mb-1">
                                                                    <h6 className="mb-1 text-primary">
                                                                        <Icon.MapPin size={16} className="me-1" />
                                                                        Origen
                                                                    </h6>
                                                                </div>
                                                                {stop.origin_postal_code && (
                                                                    <Badge bg="secondary" className="mb-1">C.P. {stop.origin_postal_code}</Badge>
                                                                )}
                                                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                    {stop.origin_address || 'Sin dirección'}
                                                                </div>
                                                            </Col>

                                                            <Col md={2} className="text-center">
                                                                <div className="d-flex align-items-center justify-content-center">
                                                                    <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                    <div className="mx-2">
                                                                        <Icon.Truck size={24} className="text-primary" />
                                                                    </div>
                                                                    <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                </div>
                                                                {stop.distance_km && (
                                                                    <div className="mt-1">
                                                                        <Badge bg="info">{stop.distance_km} km</Badge>
                                                                    </div>
                                                                )}
                                                            </Col>

                                                            <Col md={5} className="text-center">
                                                                <div className="mb-1">
                                                                    <h6 className="mb-1 text-success">
                                                                        <Icon.Flag size={16} className="me-1" />
                                                                        Destino
                                                                    </h6>
                                                                </div>
                                                                {stop.destination_postal_code && (
                                                                    <Badge bg="secondary" className="mb-1">C.P. {stop.destination_postal_code}</Badge>
                                                                )}
                                                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                    {stop.destination_address || 'Sin dirección'}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>

                                {stops.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <Icon.MapPin size={32} className="mb-2" />
                                        <p>No hay paradas agregadas</p>
                                        <p className="small">Usa el botón de arriba para agregar una parada</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        <div className="text-end">
                            <Button variant="secondary" onClick={props.onHide} className="me-2">
                                Cancelar
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Guardando...' : 'Guardar Ruta'}
                            </Button>
                        </div>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
    );
}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        let add_new_route = () => {
            this.props.setData();
            this.setState({ modalShow: false });
        }

        return (
            <ButtonToolbar>
                <Button
                    variant="primary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    Agregar Ruta
                </Button>
                <DefaultModal
                    show={this.state.modalShow}
                    onHide={add_new_route}
                    setData={this.props.setData}
                />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
